<template>
  <div class="kpi-drill-down">
    <div class="drill-summary">
      <div class="summary-count">{{ achievedCount }} / {{ totalCount }}</div>
      <div class="summary-label">{{ summaryLabel }}</div>
    </div>

    <div v-if="groupNames.length > 1" class="customer-tabs">
      <button
        v-for="groupName in groupNames"
        :key="groupName"
        type="button"
        class="customer-tab"
        :class="{ active: activeGroup === groupName }"
        @click="activeGroup = groupName"
      >
        <span>{{ groupName }}{{ $t('kpi_drill_customer_level_suffix') }}</span>
        <span class="tab-count">{{ (groups[groupName] || []).length }}</span>
      </button>
    </div>

    <div class="customer-list">
      <div
        v-for="customer in sortedCustomers"
        :key="customer.physician_id"
        class="customer-card"
      >
        <div class="customer-avatar-wrap">
          <img :src="avatarFor(customer)" class="customer-avatar" alt="" />
        </div>

        <div class="customer-info">
          <div class="customer-name-row">
            <span class="customer-name">{{ customer.physician_name || '--' }}</span>
            <span v-if="activeGroup !== 'all'" class="customer-level">{{ activeGroup }}</span>
          </div>
          <div v-if="customer.department_name || customer.professional_title" class="customer-meta">
            {{ customer.department_name }} {{ customer.professional_title }}
          </div>
          <div v-if="customer.institution_name" class="customer-institution">
            {{ customer.institution_name }}
          </div>
          <div v-if="mode !== 'binding'" class="customer-progress">
            {{ customerProgressLabel(customer) }}
          </div>
        </div>

        <div class="customer-status">
          <span
            class="status-chip"
            :class="{ achieved: isAchieved(customer), pending: !isAchieved(customer) }"
          >
            <van-icon
              :name="isAchieved(customer) ? 'checked' : 'clock-o'"
              size="12"
            />
            {{ statusLabel(customer) }}
          </span>
          <button
            v-if="mode !== 'binding' && !isAchieved(customer)"
            type="button"
            class="create-visit-button"
            @click="$emit('create-visit', customer)"
          >
            {{ $t('ai_action_plan_create_visit') }}
          </button>
        </div>
      </div>

      <div v-if="!sortedCustomers.length" class="empty-state">
        <van-icon name="friends-o" size="34" />
        <span>{{ $t('no_data') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from 'vant';

export default {
  name: 'KPIDrillDown',
  components: {
    [Icon.name]: Icon
  },
  props: {
    groups: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      default: 'coverage'
    }
  },
  data() {
    const groupNames = Object.keys(this.groups);

    return {
      activeGroup: groupNames[0] || 'all',
      portrait: require('@/assets/lundbeck/avatar-unknown.png'),
      portraitMale: require('@/assets/lundbeck/avatar-male.png'),
      portraitFemale: require('@/assets/lundbeck/avatar-female.png')
    };
  },
  computed: {
    groupNames() {
      return Object.keys(this.groups);
    },
    activeCustomers() {
      return this.groups[this.activeGroup] || [];
    },
    sortedCustomers() {
      return [...this.activeCustomers].sort((left, right) => {
        const statusDifference = Number(this.isAchieved(left)) - Number(this.isAchieved(right));
        if (statusDifference !== 0) return statusDifference;
        return String(left.physician_name || '').localeCompare(String(right.physician_name || ''));
      });
    },
    totalCount() {
      return this.activeCustomers.length;
    },
    achievedCount() {
      return this.activeCustomers.filter(customer => this.isAchieved(customer)).length;
    },
    summaryLabel() {
      if (this.mode === 'binding') return this.$t('kpi_drill_bind_summary');
      if (this.mode === 'frequency') return this.$t('kpi_drill_frequency_summary');
      return this.$t('kpi_drill_coverage_summary');
    }
  },
  watch: {
    groups: {
      handler(groups) {
        const groupNames = Object.keys(groups);
        if (!groupNames.includes(this.activeGroup)) {
          this.activeGroup = groupNames[0] || 'all';
        }
      },
      deep: true
    }
  },
  methods: {
    avatarFor(customer) {
      if (customer.sex === '男' || customer.sex === 'Male') return this.portraitMale;
      if (customer.sex === '女' || customer.sex === 'Female') return this.portraitFemale;
      return this.portrait;
    },
    isAchieved(customer) {
      if (this.mode === 'binding') return customer.isBound;
      if (this.mode === 'frequency') {
        return customer.monthlyCallCount >= customer.requiredCallCount;
      }
      return customer.isCovered;
    },
    statusLabel(customer) {
      if (this.mode === 'binding') {
        return this.$t(customer.isBound ? 'wechat_bind_bound' : 'wechat_bind_unbound');
      }
      if (this.mode === 'frequency') {
        return this.$t(this.isAchieved(customer) ? 'kpi_drill_achieved' : 'kpi_drill_pending');
      }
      return this.$t(customer.isCovered ? 'covered' : 'not_covered');
    },
    customerProgressLabel(customer) {
      if (this.mode === 'frequency') {
        return this.$t('kpi_drill_frequency_progress', {
          current: customer.monthlyCallCount,
          target: customer.requiredCallCount
        });
      }
      return this.$t('kpi_drill_monthly_visits', {
        count: customer.monthlyCallCount
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.kpi-drill-down {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.drill-summary {
  margin: 14px 14px 12px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, #fff 0%, #f1f8f5 100%);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);

  .summary-count {
    color: #111827;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
  }

  .summary-label {
    margin-top: 5px;
    color: #64748b;
    font-size: 12px;
  }
}

.customer-tabs {
  display: flex;
  gap: 8px;
  padding: 0 14px 12px;

  .customer-tab {
    flex: 1;
    height: 38px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    color: #64748b;
    font-size: 12px;

    &.active {
      border-color: #11ad6c;
      background: rgba(17, 173, 108, 0.1);
      color: #087b4c;
      font-weight: 700;
    }
  }

  .tab-count {
    margin-left: 3px;
    font-size: 11px;
  }
}

.customer-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 14px 22px;
}

.customer-card {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 76px;
  margin-bottom: 10px;
  padding: 12px;
  border: 1px solid #edf0f4;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
}

.customer-avatar-wrap {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  overflow: hidden;
  border-radius: 50%;
  background: linear-gradient(165deg, #dce1f0, #f6eff1);
}

.customer-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.72;
}

.customer-info {
  flex: 1;
  min-width: 0;
}

.customer-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.customer-name {
  overflow: hidden;
  color: #172033;
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-level {
  min-width: 18px;
  padding: 2px 5px;
  border-radius: 7px;
  background: #eef6ff;
  color: #2563a9;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
}

.customer-meta,
.customer-institution,
.customer-progress {
  overflow: hidden;
  margin-top: 4px;
  color: #7a8495;
  font-size: 11px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-institution {
  color: #9aa3b1;
}

.customer-progress {
  color: #64748b;
}

.customer-status {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 7px;
  border-radius: 999px;
  font-size: 10px;
  white-space: nowrap;

  &.achieved {
    background: #e8f8f0;
    color: #0c8a56;
  }

  &.pending {
    background: #fff3e8;
    color: #dc6a22;
  }
}

.create-visit-button {
  min-width: 68px;
  padding: 6px 9px;
  border: 0;
  border-radius: 9px;
  background: #aa341b;
  color: #fff;
  font-size: 11px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 180px;
  color: #a5adba;
  font-size: 12px;
}
</style>
